// Implements: F-GEN-033, F-GEN-034, F-UIX-025, F-UIX-009
/**
 * Drawing ONE diagram kind of ONE version, as a small state machine, so no screen can reach the
 * paid call by skipping the warning (mockup v3 P6).
 *
 *   idle -> running -> done | failed                                  (journey)
 *   idle -> estimating -> confirming -> running -> done | failed      (walkthrough)
 *
 * The walkthrough is the paid, optional step F-GEN-034 quotes: `estimating` runs the read-only
 * estimate for that kind, `confirming` holds the quote until the person decides, and only
 * `confirm()` moves on to the run; `dismiss()` goes back to `idle` having paid nothing. With the
 * deployment flag `VITE_DEMO_AUTO_APPROVE` on (OD-12) the quote is still asked for and its ceiling
 * logged, and the run follows at once. A refused estimate (a `409` with its reason) stops there:
 * nothing is drawn and nothing is charged.
 *
 * Calls never reject: a failure is a state. The run is never sent again by itself (checklist 13).
 * Stale answers are ignored (checklist 14): once the screen of this version is gone, or when the
 * run answers for another version than this one, nothing here changes; the version the run drew
 * is still refreshed, so whoever reads it next sees its diagram.
 */

import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useRef, useState } from "react";

import { ApiError, UNKNOWN_ERROR_CODE } from "../../../shared/api/ApiError";
import { asApiError } from "../../../shared/api/asApiError";
import { versionCostsKey, versionDetailKey } from "../../../shared/api/queryKeys";
import { demoAutoApprove } from "../../../shared/config/demo";
import type { DiagramCostEstimate, DiagramKind, DiagramRunView } from "../../../types/diagrams";
import { estimateDiagram, refusalOf, runDiagram } from "../api/diagramsApi";

export type DiagramPhase = "idle" | "estimating" | "confirming" | "running" | "done" | "failed";

export interface DiagramState {
  readonly phase: DiagramPhase;
  readonly estimate: DiagramCostEstimate | null;
  readonly result: DiagramRunView | null;
  readonly error: ApiError | null;
}

export interface UseDiagramResult extends DiagramState {
  readonly submit: (brief: string) => void;
  readonly confirm: () => void;
  readonly dismiss: () => void;
  /** After a failure: back to the form (or, for the walkthrough, to a fresh free quote). Never a paid re-send. */
  readonly retry: () => void;
}

const IDLE: DiagramState = { phase: "idle", estimate: null, result: null, error: null };

function bodyless(what: string): ApiError {
  return new ApiError(200, UNKNOWN_ERROR_CODE, `${what} had no body`, new Headers());
}

export function useDiagram(mockupId: string, versionId: string, kind: DiagramKind): UseDiagramResult {
  const [state, setState] = useState<DiagramState>(IDLE);
  const client = useQueryClient();
  // What is waiting for the person's decision; nothing runs while it is set.
  const pending = useRef<string | null>(null);
  const last = useRef<string | null>(null);
  // A late answer to a screen that is gone changes nothing: the page keys this hook's screen by
  // version, so its state goes with it.
  const settle = setState;

  // A refusal that says the version already holds, or is getting, this diagram means the page's
  // copy of the version is stale: it is read again, so the drawn diagram shows without a reload.
  const fail = useCallback(
    (cause: unknown): void => {
      const error = asApiError(cause);
      const reason = refusalOf(error);
      if (reason === "diagram_already_drawn" || reason === "diagram_in_progress") {
        void client.invalidateQueries({ queryKey: versionDetailKey(mockupId, versionId) });
      }
      settle({ phase: "failed", estimate: null, result: null, error });
    },
    [mockupId, versionId, client, settle],
  );

  const run = useCallback(
    async (brief: string): Promise<void> => {
      settle({ phase: "running", estimate: null, result: null, error: null });
      try {
        const result = await runDiagram(mockupId, { brief, kind });
        // A 200 with nothing in it is a contract violation, not an empty success.
        if (result === undefined) throw bodyless("The diagram run");
        void client.invalidateQueries({ queryKey: versionDetailKey(mockupId, result.version_id) });
        // The costs of the mockup's versions now hold this draw (its time and cost): every
        // version-costs read of the mockup (the key's prefix, whatever ids it asked) is read again.
        void client.invalidateQueries({ queryKey: versionCostsKey(mockupId, []).slice(0, 3) });
        settle(result.version_id === versionId ? { phase: "done", estimate: null, result, error: null } : IDLE);
      } catch (cause) {
        fail(cause);
      }
    },
    [mockupId, versionId, kind, client, settle, fail],
  );

  const submit = useCallback(
    (brief: string): void => {
      last.current = brief;
      if (kind === "journey") {
        void run(brief);
        return;
      }
      settle({ phase: "estimating", estimate: null, result: null, error: null });
      estimateDiagram(mockupId, { brief, kind }).then(
        (estimate) => {
          if (estimate === undefined) {
            settle({ phase: "failed", estimate: null, result: null, error: bodyless("The estimate") });
            return;
          }
          if (demoAutoApprove()) {
            console.info(
              `demo auto-approval: ${kind} ceiling ${String(estimate.cost.amount_micros)} micros ${estimate.cost.currency}`,
            );
            void run(brief);
            return;
          }
          pending.current = brief;
          settle({ phase: "confirming", estimate, result: null, error: null });
        },
        (cause: unknown) => {
          fail(cause);
        },
      );
    },
    [mockupId, kind, run, settle, fail],
  );

  const confirm = useCallback((): void => {
    const waiting = pending.current;
    if (waiting === null) return;
    pending.current = null;
    void run(waiting);
  }, [run]);

  const dismiss = useCallback((): void => {
    pending.current = null;
    settle(IDLE);
  }, [settle]);

  const retry = useCallback((): void => {
    const previous = last.current;
    // With auto-approval the quote is not a stop, so a retry would pay again with nobody
    // having decided: it goes back to the form and the person presses again.
    if (kind === "walkthrough" && previous !== null && !demoAutoApprove()) {
      submit(previous);
      return;
    }
    dismiss();
  }, [kind, submit, dismiss]);

  return { ...state, submit, confirm, dismiss, retry };
}
